import { apiService } from '@/services/api.service';

export default {
  namespaced: true,
  state: {
    events: [],
    stadiums: [],
    isLoading: false,
    error: null,
  },
  getters: {
    getEvents: (state: any) => state.events,
    getStadiums: (state: any) => state.stadiums,
    isLoading: (state: any) => state.isLoading,
    getError: (state: any) => state.error,
  },
  mutations: {
    SET_LOADING(state: any, isLoading: boolean) {
      state.isLoading = isLoading;
    },
    SET_ERROR(state: any, error: string | null) {
      state.error = error;
    },
    SET_EVENTS(state: any, events: any[]) {
      state.events = events;
    },
    SET_STADIUMS(state: any, stadiums: any[]) {
      state.stadiums = stadiums;
    },
    ADD_EVENT(state: any, event: any) {
      state.events.push(event);
    },
    UPDATE_EVENT(state: any, updatedEvent: any) {
      const index = state.events.findIndex((event: any) => event.id === updatedEvent.id);
      if (index !== -1) {
        state.events.splice(index, 1, updatedEvent);
      }
    },
    DELETE_EVENT(state: any, eventId: string) {
      state.events = state.events.filter((event: any) => event.id !== eventId);
    },
  },
  actions: {
    async fetchEvents({ commit }: any) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      try {
        const eventsData = await apiService.getEvents();

        const events = eventsData.map((event: any) => {
          const stadium = event.stadium;
          const district = stadium?.district;
          const province = district?.parent;
          const department = province?.parent;
          const country = department?.parent;

          // Mantenemos todo el evento original y añadimos los campos extra
          return {
            ...event,
            stadiumName: stadium?.shortName || "Desconocido",
            countryName: country?.name || "Desconocido",
            countryFlag:
              country?.flag || "https://via.placeholder.com/80x60?text=No+Flag",
          };
        });

        commit("SET_EVENTS", events);
      } catch (error: any) {
        console.error("Error al obtener los eventos:", error);
        const errorMessage = error.response?.data?.message || error.message || "Error al obtener los eventos";
        commit('SET_ERROR', errorMessage);
        throw new Error(errorMessage);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchStadiums({ commit }: any) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      try {
        const stadiums = await apiService.getStadiums();
        commit("SET_STADIUMS", stadiums);
      } catch (error: any) {
        console.error("Error al obtener los estadios:", error);
        const errorMessage = error.response?.data?.message || error.message || "Error al obtener los estadios";
        commit('SET_ERROR', errorMessage);
        throw new Error(errorMessage);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createEvent({ commit }: any, newEvent: any) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      try {
        const createdEvent = await apiService.createEvent(newEvent);
        commit('ADD_EVENT', createdEvent);
        return createdEvent;
      } catch (error: any) {
        console.error("Error al crear el evento:", error);
        const errorMessage = error.response?.data?.message || error.message || "Error al crear el evento";
        commit('SET_ERROR', errorMessage);
        throw new Error(errorMessage);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updateEvent({ commit }: any, { eventId, eventData }: { eventId: string; eventData: any }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      try {
        const updatedEvent = await apiService.updateEvent(eventId, eventData);
        commit('UPDATE_EVENT', updatedEvent);
        return updatedEvent;
      } catch (error: any) {
        console.error("Error al actualizar el evento:", error);
        const errorMessage = error.response?.data?.message || error.message || "Error al actualizar el evento";
        commit('SET_ERROR', errorMessage);
        throw new Error(errorMessage);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async deleteEvent({ commit }: any, eventId: string) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      try {
        await apiService.deleteEvent(eventId);
        commit("DELETE_EVENT", eventId);
      } catch (error: any) {
        console.error("Error al eliminar el evento:", error);
        const errorMessage = error.response?.data?.message || error.message || "Error al eliminar el evento";
        commit('SET_ERROR', errorMessage);
        throw new Error(errorMessage);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async uploadEventLogo({ commit }: any, { eventId, file }: { eventId: string; file: File }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const result = await apiService.uploadEventLogo(eventId, file);
        return result;
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Error al subir el logo';
        commit('SET_ERROR', errorMessage);
        throw new Error(errorMessage);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async deleteEventLogo({ commit }: any, eventId: string) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const result = await apiService.deleteEventLogo(eventId);
        return result;
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Error al eliminar el logo';
        commit('SET_ERROR', errorMessage);
        throw new Error(errorMessage);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Limpiar errores
    clearError({ commit }: any) {
      commit('SET_ERROR', null);
    },
  },
};
